import React, { useMemo, useState } from 'react'
import SelectBox from '../../../components/SelectBox'
import {
  parlayEnhancementIdeas,
  parlayTotalsByType,
  parlayTypeOptions,
  parlayVoucherFeed,
} from '../../../utils/parlayVoucherInsightsDemoData'

const formatCurrency = (value) =>
  value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  })

const formatTimestamp = (value) =>
  new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value))

const getMobileFlag = () => {
  if (typeof window === 'undefined') return false
  return window.innerWidth <= 768
}

const LiveBettingAutomationPage = (demo = true) => {
  const [dateRange, setDateRange] = useState({ from: '', to: '' })
  const [searchTerm, setSearchTerm] = useState('')
  const [parlaySelection, setParlaySelection] = useState(parlayTypeOptions[0])
  const isMobile = getMobileFlag()

  const totalsList = useMemo(
    () => Array.from(parlayTotalsByType.values()).sort((a, b) => b.totalStake - a.totalStake),
    [],
  )

  const filteredVouchers = useMemo(() => {
    const fromDate = dateRange.from ? new Date(dateRange.from) : null
    const toDate = dateRange.to ? new Date(dateRange.to) : null
    const query = searchTerm.trim().toLowerCase()

    return parlayVoucherFeed.filter((voucher) => {
      if (parlaySelection.value !== 'all' && voucher.parlaySize !== parlaySelection.value) {
        return false
      }

      if (fromDate && Number.isFinite(fromDate.valueOf())) {
        if (new Date(voucher.createdAt) < fromDate) return false
      }

      if (toDate && Number.isFinite(toDate.valueOf())) {
        if (new Date(voucher.createdAt) > toDate) return false
      }

      if (!query) return true

      return (
        voucher.customerName.toLowerCase().includes(query) ||
        voucher.username.toLowerCase().includes(query) ||
        voucher.id.toLowerCase().includes(query)
      )
    })
  }, [parlaySelection, dateRange, searchTerm])

  const summary = useMemo(() => {
    if (filteredVouchers.length === 0) {
      return {
        totalStake: 0,
        totalPotential: 0,
        averageLegs: 0,
      }
    }

    const totals = filteredVouchers.reduce(
      (accumulator, voucher) => {
        accumulator.totalStake += voucher.stake
        accumulator.totalPotential += voucher.potential
        accumulator.totalLegs += voucher.parlaySize
        return accumulator
      },
      { totalStake: 0, totalPotential: 0, totalLegs: 0 },
    )

    return {
      totalStake: totals.totalStake,
      totalPotential: totals.totalPotential,
      averageLegs: totals.totalLegs / filteredVouchers.length,
    }
  }, [filteredVouchers])

   if (demo) {
    return <div className="col-span-full flex items-center justify-center rounded-3xl border border-dashed border-customMuted dark:border-custom_dark_secondary bg-white dark:bg-custom_dark_primary2 py-20 text-center text-customMuted">
      These features are coming soon in a future update!
    </div>
  }

  return (
    <div className="flex flex-col gap-6">
      <section className="rounded-2xl border border-customAccentBlue/20 bg-white p-5 shadow-lg shadow-customAccentBlue/15 dark:border-customSurface dark:bg-customSurface">
        <header className="mb-4 flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-customAccentBlue dark:text-customHeading">
              Insights Mix Parlay Feed
            </p>
            <h2 className="text-2xl font-bold text-customPrimaryText dark:text-customHeading">
              Parlay vouchers ranked by exposure impact
            </h2>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">
              Slice by parlay size, spot large liabilities, and drill into voucher legs before they settle.
            </p>
          </div>
          <div className="flex flex-col items-start gap-2 rounded-2xl bg-customAccentBlue/10 px-4 py-3 text-xs text-customAccentBlue dark:bg-custom_dark_primary2/50 dark:text-customHeading">
            <span>
              Stake sum:{' '}
              <strong className="font-semibold text-customAccentBlue dark:text-customHeading">
                {formatCurrency(summary.totalStake || 0)}
              </strong>
            </span>
            <span>
              Potential payout:{' '}
              <strong className="font-semibold text-customAccentBlue dark:text-customHeading">
                {formatCurrency(summary.totalPotential || 0)}
              </strong>
            </span>
            <span>
              Avg legs:{' '}
              <strong className="font-semibold text-customAccent dark:text-customHeading">
                {summary.averageLegs.toFixed(1)}
              </strong>
            </span>
          </div>
        </header>

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="flex flex-col gap-2 rounded-2xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            From
            <input
              type="date"
              value={dateRange.from}
              onChange={(event) =>
                setDateRange((prev) => ({
                  ...prev,
                  from: event.target.value,
                }))
              }
              className="rounded-xl border border-transparent bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/40 dark:bg-customSurface dark:text-customHeading"
            />
          </label>
          <label className="flex flex-col gap-2 rounded-2xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            To
            <input
              type="date"
              value={dateRange.to}
              onChange={(event) =>
                setDateRange((prev) => ({
                  ...prev,
                  to: event.target.value,
                }))
              }
              className="rounded-xl border border-transparent bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/40 dark:bg-customSurface dark:text-customHeading"
            />
          </label>
          <label className="flex flex-col gap-2 rounded-2xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            Search voucher or user
            <input
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder="e.g. MP-241016"
              className="rounded-xl border border-transparent bg-white px-3 py-2 text-sm text-customPrimaryText outline-none transition focus:border-customAccent focus:ring-2 focus:ring-customAccent/40 dark:bg-customSurface dark:text-customHeading"
            />
          </label>
          <div className="flex flex-col gap-2 rounded-2xl border border-customAccentBlue/20 bg-customLightMilkWhite px-4 py-3 text-sm text-customSecondaryText dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customMuted">
            Parlay type
            <SelectBox
              selected={parlaySelection}
              setSelected={setParlaySelection}
              options={parlayTypeOptions}
              placeholder="Select parlay type"
              isMobile={isMobile}
            />
          </div>
        </div>
      </section>

      <section className="rounded-2xl border border-customAccentBlue/20 bg-white p-5 shadow-md shadow-customAccentBlue/15 dark:border-customSurface dark:bg-customSurface">
        <header className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-customPrimaryText dark:text-customHeading">
              Total betting by parlay type
            </h3>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">
              Understand stake distribution to adjust risk limits before high-liability slips go live.
            </p>
          </div>
          <span className="rounded-full bg-customAccentBlue/10 px-4 py-1 text-xs font-semibold uppercase tracking-wide text-customAccentBlue dark:bg-customAccent/20 dark:text-customHeading">
            {totalsList.length} types tracked
          </span>
        </header>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          {totalsList.map((item) => (
            <article
              key={item.type}
              className="rounded-2xl border border-customAccentBlue/20 bg-customAccentBlue/10 px-4 py-3 text-sm text-customPrimaryText shadow-sm dark:border-customSurfaceMuted dark:bg-customSurfaceMuted dark:text-customHeading"
            >
              <h4 className="text-xs font-semibold uppercase tracking-wide text-customSecondaryText dark:text-customMuted">
                {item.type}
              </h4>
              <p className="mt-2 text-xs text-customSecondaryText dark:text-customMuted">
                {item.vouchers} vouchers
              </p>
              <p className="mt-1 text-base font-bold text-customAccentBlue dark:text-customHeading">
                {formatCurrency(item.totalStake)}
              </p>
              <p className="text-xs text-customSecondaryText dark:text-customMuted">
                Potential {formatCurrency(item.totalPotential)}
              </p>
            </article>
          ))}
        </div>
      </section>

      <section className="overflow-hidden rounded-2xl border border-customAccentBlue/20 bg-white shadow-lg shadow-customAccentBlue/10 dark:border-customSurface dark:bg-customSurface">
        <header className="flex flex-wrap items-center justify-between gap-3 border-b border-customAccentBlue/10 px-5 py-4 dark:border-customSurfaceMuted">
          <div>
            <h3 className="text-lg font-semibold text-customPrimaryText dark:text-customHeading">
              Mix parlay vouchers
            </h3>
            <p className="text-sm text-customSecondaryText dark:text-customMuted">
              Sorted by highest potential payout to help you prioritise manual reviews.
            </p>
          </div>
          <button
            type="button"
            className="rounded-xl border border-transparent bg-customPredawn px-4 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-md shadow-customPredawn/30 transition hover:bg-customPredawn/90 focus:outline-none focus:ring-2 focus:ring-customPredawn/60 focus:ring-offset-2 dark:bg-customAccent dark:hover:bg-customAccent/80"
          >
            Export vouchers
          </button>
        </header>

        <div className="relative overflow-x-auto">
          <table className="min-w-full divide-y divide-customAccent/10 text-sm dark:divide-customSurfaceMuted">
            <thead className="bg-background text-left text-xs font-semibold uppercase tracking-wide text-customLightMilkWhite dark:bg-custom_dark_primary2/60 dark:text-customMuted">
              <tr>
                <th className="px-4 py-3">No</th>
                <th className="px-4 py-3">Voucher</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Stake</th>
                <th className="px-4 py-3">Potential</th>
                <th className="px-4 py-3 text-center">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-customAccent/5 bg-white dark:divide-customSurfaceMuted/80 dark:bg-customSurface">
              {filteredVouchers.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-4 py-8 text-center text-sm font-medium text-customSecondaryText dark:text-customMuted"
                  >
                    No vouchers match the current filters. Try adjusting the parlay type or date range.
                  </td>
                </tr>
              ) : (
                filteredVouchers
                  .slice()
                  .sort((a, b) => b.potential - a.potential)
                  .map((voucher, index) => (
                    <tr
                      key={voucher.id}
                      className="transition hover:bg-customAccentBlue/5 dark:hover:bg-customSurfaceMuted/70"
                    >
                      <td className="px-4 py-1 text-xs font-semibold text-customSecondaryText dark:text-customMuted">
                        {String(index + 1).padStart(2, '0')}
                      </td>
                      <td className="px-4 py-1 text-sm font-semibold text-customPrimaryText dark:text-customHeading">
                        <div className="flex flex-col">
                          <span>{voucher.id}</span>
                          <span className="text-xs text-customSecondaryText dark:text-customMuted">
                            {formatTimestamp(voucher.createdAt)} · Parlay {voucher.parlaySize}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-1 text-sm text-customPrimaryText dark:text-customHeading">
                        <div className="flex flex-col">
                          <span className="font-semibold text-customAccent dark:text-customHeading">
                            {voucher.customerName}
                          </span>
                          <span className="text-xs text-customSecondaryText dark:text-customMuted">
                            @{voucher.username}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-1 text-sm text-customAccent dark:text-customHeading">
                        {formatCurrency(voucher.stake)}
                      </td>
                      <td className="px-4 py-1 text-sm text-customAccentBlue dark:text-customHeading">
                        {formatCurrency(voucher.potential)}
                      </td>
                      <td className="px-4 py-1 text-center">
                        <div className="flex flex-col items-center justify-center gap-2">
                          <button
                            type="button"
                            className="w-full rounded-xl border border-transparent bg-customPredawn px-3 py-2 text-xs font-semibold uppercase tracking-wide text-white shadow-sm shadow-customPredawn/30 transition hover:bg-customPredawn/90 focus:outline-none focus:ring-2 focus:ring-customPredawn/60 focus:ring-offset-2 dark:bg-customAccent dark:hover:bg-customAccent/80"
                          >
                            View details
                          </button>
                          <button
                            type="button"
                            className="w-full rounded-xl border border-customPredawn/30 px-3 py-2 text-[10px] font-semibold uppercase tracking-wide text-customPredawn transition hover:border-customPredawn/50 hover:bg-customPredawn/10 dark:border-customSurfaceMuted dark:bg-custom_dark_primary2 dark:text-customHeading"
                          >
                            Build hedge
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {/* <section className="rounded-2xl border border-dashed border-customAccent/30 bg-customSlate/70 p-5 text-sm shadow-inner dark:border-customSurfaceMuted dark:bg-customSurfaceMuted/70 dark:text-customMuted">
        <h3 className="text-base font-semibold text-customPrimaryText dark:text-customHeading">
          Suggested automation ideas
        </h3>
        <ul className="mt-3 grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {parlayEnhancementIdeas.map((idea, index) => (
            <li
              key={idea}
              className="rounded-xl bg-white px-3 py-3 text-sm font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading"
            >
              <span className="mr-2 text-xs font-semibold text-customAccent dark:text-customHeading">
                #{index + 1}
              </span>
              {idea}
            </li>
          ))}
          <li className="rounded-xl bg-white px-3 py-3 text-sm font-medium text-customPrimaryText shadow-sm dark:bg-customSurface dark:text-customHeading">
            Tie in live cash-out odds so supervisors can pre-approve profitable exits automatically.
          </li>
        </ul>
      </section> */}
    </div>
  )
}

export default LiveBettingAutomationPage
